/**
 * Tech availability — working days, booked time off, company holidays —
 * resolved to a per-tech, per-day answer the board and My Day can render.
 *
 * Soft by design (agreed 2026-09-23): the board shades an unavailable tech
 * and warns on a drop, but never blocks. There are real reasons to schedule
 * someone on a day off (a Saturday they agreed to, a cancelled holiday).
 */

import { useQuery } from '@tanstack/react-query';
import { api, type CompanyHoliday, type Tech, type TechTimeOff } from './api.js';
import { compactHourLabel } from './dispatch-board.js';

export type Unavailable =
  | { kind: 'holiday'; label: string }
  | {
      kind: 'time_off';
      label: string;
      reason: string | null;
      endDate: string;
      /** Clock hours (fractional) the tech is off; null = the whole day. */
      hours: { start: number; end: number } | null;
    }
  | { kind: 'day_off'; label: string };

export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** 0 = Sunday .. 6 = Saturday, for a YYYY-MM-DD in LOCAL time (no tz drift). */
export function weekdayOf(isoDate: string): number {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1).getDay();
}

/**
 * Why a tech is unavailable on `date`, or null when they're working.
 * Priority: company holiday > booked time off > regular day off.
 */
export function unavailability(
  tech: Pick<Tech, 'workDays'>,
  date: string,
  timeOff: ReadonlyArray<
    Pick<TechTimeOff, 'startDate' | 'endDate' | 'reason'> & Partial<Pick<TechTimeOff, 'startTime' | 'endTime'>>
  >,
  holidays: ReadonlyArray<CompanyHoliday>,
): Unavailable | null {
  const holiday = holidays.find((h) => h.date === date);
  if (holiday) return { kind: 'holiday', label: holiday.name };
  const matches = timeOff.filter((t) => t.startDate <= date && date <= t.endDate);
  // A whole-day entry outranks a part-day one booked on the same date.
  const off = matches.find((t) => clockWindow(t) === null) ?? matches[0];
  if (off) {
    const reason = off.reason?.trim() || null;
    const hours = clockWindow(off);
    const until = off.endDate !== off.startDate ? ` to ${shortDate(off.endDate)}` : '';
    const when = hours ? ` ${compactHourLabel(hours.start)}–${compactHourLabel(hours.end)}` : '';
    return { kind: 'time_off', label: `${reason ?? 'Off'}${when}${until}`, reason, endDate: off.endDate, hours };
  }
  if (!tech.workDays.includes(weekdayOf(date))) {
    return { kind: 'day_off', label: `Off — ${DAY_NAMES[weekdayOf(date)]}` };
  }
  return null;
}

/** "08:00"/"12:30" → fractional clock hours, or null for a whole-day entry. */
function clockWindow(t: Partial<Pick<TechTimeOff, 'startTime' | 'endTime'>>): { start: number; end: number } | null {
  const start = clockToHour(t.startTime);
  const end = clockToHour(t.endTime);
  return start !== null && end !== null && end > start ? { start, end } : null;
}

function clockToHour(v: string | null | undefined): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(v ?? '');
  return m ? Number(m[1]) + Number(m[2]) / 60 : null;
}

/** True when the tech is off for the whole of the day (anything but a part-day entry). */
export function isWholeDayOff(u: Unavailable): boolean {
  return !(u.kind === 'time_off' && u.hours !== null);
}

/** Does a block starting at `startHour` for `duration` hours fall in the tech's time off? */
export function offDuring(u: Unavailable | null | undefined, startHour: number, duration: number): boolean {
  if (!u) return false;
  if (u.kind !== 'time_off' || u.hours === null) return true;
  return startHour < u.hours.end && startHour + duration > u.hours.start;
}

function shortDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Time off + holidays for a window of days around `date` (one query each, cached). */
export function useAvailabilityWindow(fromDate: string, toDate: string, techId?: string) {
  const timeOff = useQuery({
    queryKey: ['availability', 'time-off', fromDate, toDate, techId ?? 'all'],
    queryFn: ({ signal }) =>
      api.admin.techs.timeOff({ from: fromDate, to: toDate, ...(techId ? { techId } : {}) }, signal),
    staleTime: 60_000,
  });
  const holidays = useQuery({
    queryKey: ['availability', 'holidays', fromDate, toDate],
    queryFn: ({ signal }) => api.admin.techs.holidays({ from: fromDate, to: toDate }, signal),
    staleTime: 5 * 60_000,
  });
  return {
    timeOff: timeOff.data ?? [],
    holidays: holidays.data ?? [],
    isPending: timeOff.isPending || holidays.isPending,
  };
}

/** Human list of who is out on a date: "2 out: Marcus (Holiday), Diego (Off — Sat)". */
export function whoIsOut(
  techs: ReadonlyArray<Pick<Tech, 'id' | 'name' | 'workDays'>>,
  date: string,
  timeOff: ReadonlyArray<TechTimeOff>,
  holidays: ReadonlyArray<CompanyHoliday>,
): Array<{ techId: string; name: string; why: Unavailable }> {
  return techs.flatMap((t) => {
    const why = unavailability(t, date, timeOff.filter((o) => o.techId === t.id), holidays);
    return why ? [{ techId: t.id, name: t.name, why }] : [];
  });
}
